import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, Loader, AlertCircle, ArrowLeft } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { getAllPrograms } from '../services/programService';
import ProgramCard from '../components/ProgramCard';
import ProgramUploadModal from '../components/ProgramUploadModal';
import AlertModal from '../components/AlertModal';
import NotificationCenter from '../components/NotificationCenter';

const OurPrograms = () => {
  const navigate = useNavigate();
  const { currentUser, userRole } = useContext(AuthContext);
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);

  const isAdmin = ['master_admin', 'overseer', 'colony_prime_minister'].includes(userRole);

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    filterPrograms();
  }, [programs, searchTerm]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllPrograms();
      setPrograms(data);
    } catch (err) {
      setError(err.message || 'Failed to load programs');
      console.error('Error loading programs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterPrograms = () => {
    if (!searchTerm.trim()) {
      setFilteredPrograms(programs);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = programs.filter(
        (program) =>
          program.title.toLowerCase().includes(term) ||
          program.description.toLowerCase().includes(term)
      );
      setFilteredPrograms(filtered);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleUploadSuccess = () => {
    fetchPrograms();
  };

  const handleAlertSuccess = () => {
    // Show success message
    alert('Alert sent to all users successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
              >
                <ArrowLeft size={20} />
                Back
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Our Programs</h1>
                <p className="text-gray-600 mt-1">Discover and engage with our latest initiatives</p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Notification Center */}
              <div className="relative">
                <NotificationCenter
                  isOpen={notificationOpen}
                  onClose={setNotificationOpen}
                />
              </div>

              {/* Admin Actions */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    <Plus size={20} />
                    <span>Upload Program</span>
                  </button>
                  <button
                    onClick={() => setShowAlertModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                  >
                    <AlertCircle size={20} />
                    <span>Send Alert</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search programs..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Filter className="absolute left-3 top-3 text-gray-400" size={18} />
            </div>
            <span className="text-sm text-gray-600 font-medium">
              {filteredPrograms.length} program{filteredPrograms.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader className="animate-spin mx-auto text-blue-600 mb-4" size={32} />
              <p className="text-gray-600">Loading programs...</p>
            </div>
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No programs found</h2>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? 'Try adjusting your search criteria'
                : 'Check back soon for upcoming programs!'}
            </p>
            {isAdmin && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Plus size={20} />
                Upload Your First Program
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrograms.map((program) => (
              <ProgramCard
                key={program.id}
                program={program}
                onRefresh={fetchPrograms}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ProgramUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
      />
      <AlertModal
        isOpen={showAlertModal}
        onClose={() => setShowAlertModal(false)}
        onSuccess={handleAlertSuccess}
      />
    </div>
  );
};

export default OurPrograms;
